import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useTour } from "../components/tour/TourProvider";
import { useSidebar } from "../app/App";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { getNotifications, markAsRead, markAllAsRead, Notification } from "../services/notifications";
import { formatDate } from "../lib/dateFormat";
import RoleModeSwitcher from "../components/RoleModeSwitcher";

/**
 * Light, club-scoped top bar. Replaces the dark global FrontendLayout header
 * on /dashboard/admin-club routes and carries exactly the controls the club
 * admin needs there: the mobile sidebar toggle, the language switcher,
 * notifications, and the current account (profile + logout). Owned by the
 * club layout so it matches the light club UI rather than the dark shell.
 */
export default function ClubTopbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const tour = useTour();
  const { toggleClubAdminMenu } = useSidebar();

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notifRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotificationsOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await getNotifications({ limit: 10 });
      setNotifications(data.notifications);
      setUnreadCount(data.unreadCount);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, 30000);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  const handleMarkAsRead = async (n: Notification) => {
    if (!n.read_at) {
      try {
        await markAsRead(n.id);
        setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)));
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch (err) {
        console.error("Failed to mark as read:", err);
      }
    }
    setNotificationsOpen(false);
    // Navigate only when the notification carries a destination; there is no
    // standalone /notifications route, so a blind navigate() bounced the admin
    // to the dashboard.
    if (n.payload?.action_url) {
      navigate(n.payload.action_url);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  };

  const timeAgo = (dateString: string) => {
    const diffMs = Date.now() - new Date(dateString).getTime();
    const mins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);
    if (mins < 1) return t("time.just_now", "acum");
    if (mins < 60) return t("time.minutes_ago", "{{count}} min", { count: mins });
    if (hours < 24) return t("time.hours_ago", "{{count}}h", { count: hours });
    if (days < 7) return t("time.days_ago", "{{count}}z", { count: days });
    return formatDate(new Date(dateString));
  };

  const initial = user?.full_name?.charAt(0) || user?.email?.charAt(0) || "?";

  return (
    <header className="sticky top-0 z-40 bg-slate-900">
      <div className="flex items-center justify-between h-14 px-3 sm:px-4">
        {/* Left: mobile menu toggle + brand */}
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={toggleClubAdminMenu}
            className="lg:hidden p-2 -ml-1 rounded-lg text-slate-200 hover:bg-slate-800 transition-colors"
            aria-label={t("common.open_menu", "Deschide meniul")}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <Link to="/dashboard/admin-club" className="flex items-center">
            <span className="text-lg font-bold text-white">
              <span className="text-emerald-400">i</span>GROWth
            </span>
          </Link>

          {/* Guided tour. Beside the logo rather than at the bottom of the
              sidebar, so it is reachable from every page — including the ones
              someone is most likely to be lost on. */}
          <button
            type="button"
            onClick={() => tour.start()}
            title={t("tour.restart", "Tur ghidat")}
            aria-label={t("tour.restart", "Tur ghidat")}
            className="group relative ml-1 p-2 rounded-lg transition-colors hover:bg-amber-400/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60"
          >
            {/* The glow the bulb casts once it is on. Behind the icon and
                pointer-events-none, so it never intercepts the click. */}
            <span
              aria-hidden
              className="absolute inset-0 rounded-lg opacity-0 transition-opacity duration-300 pointer-events-none bg-amber-300/25 blur-md group-hover:opacity-100 group-focus-visible:opacity-100"
            />
            <svg
              className="relative w-5 h-5 transition-all duration-200 text-slate-300 group-hover:text-amber-300 group-focus-visible:text-amber-300 group-hover:drop-shadow-[0_0_6px_rgba(252,211,77,0.9)] group-focus-visible:drop-shadow-[0_0_6px_rgba(252,211,77,0.9)]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 18h6M10 21h4M12 3a6 6 0 00-3.6 10.8c.5.37.8.95.85 1.57l.02.63h5.46l.02-.63c.05-.62.35-1.2.85-1.57A6 6 0 0012 3z"
              />
            </svg>
          </button>
        </div>

        {/* Right: language, notifications, account */}
        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSwitcher variant="dark" bare />

          {/* Notifications */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => setNotificationsOpen((v) => !v)}
              className="relative p-2 rounded-lg text-slate-200 border border-transparent hover:bg-white/10 transition-colors"
              aria-label={t("notifications.title", "Notificări")}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-rose-500 rounded-full">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>

            {notificationsOpen && (
              <div className="absolute right-0 mt-2 overflow-hidden bg-white border border-slate-200 shadow-xl top-full w-80 sm:w-96 rounded-2xl">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-800">{t("notifications.title", "Notificări")}</h3>
                  {unreadCount > 0 && (
                    <button onClick={handleMarkAllAsRead} className="text-xs font-medium text-indigo-600 hover:text-indigo-700">
                      {t("notifications.mark_all_read", "Marchează toate citite")}
                    </button>
                  )}
                </div>
                <div className="overflow-y-auto max-h-96">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-10 text-center">
                      <svg className="w-10 h-10 mx-auto mb-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      <p className="text-sm text-slate-400">{t("notifications.empty", "Nicio notificare încă")}</p>
                    </div>
                  ) : (
                    notifications.map((n) => (
                      <button
                        key={n.id}
                        onClick={() => handleMarkAsRead(n)}
                        className={`w-full px-4 py-3 text-left border-b border-slate-100 hover:bg-slate-50 transition-colors ${!n.read_at ? "bg-indigo-50/40" : ""}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`mt-2 w-2 h-2 rounded-full flex-shrink-0 ${!n.read_at ? "bg-indigo-500" : "bg-transparent"}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${!n.read_at ? "text-slate-800" : "text-slate-600"}`}>
                              {n.title || n.template_code}
                            </p>
                            <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{n.payload?.message || ""}</p>
                            <p className="mt-1 text-xs text-slate-400">{timeAgo(n.created_at)}</p>
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
                {notifications.length > 0 && (
                  <div className="px-4 py-2.5 border-t border-slate-100">
                    <Link to="/notifications" className="text-sm font-medium text-indigo-600 hover:text-indigo-700" onClick={() => setNotificationsOpen(false)}>
                      {t("notifications.view_all", "Vezi toate notificările")}
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Account */}
          <div className="relative" ref={profileRef}>
            <button
              onClick={() => setProfileOpen((v) => !v)}
              className="flex items-center gap-2 p-1 pr-1.5 sm:pr-2 rounded-full bg-slate-800 border border-slate-700 hover:bg-slate-700 transition-colors"
            >
              <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500">
                <span className="text-xs font-bold text-white">{initial}</span>
              </div>
              <span className="hidden md:block text-slate-200 text-sm font-medium max-w-[110px] truncate">
                {user?.full_name || user?.email?.split("@")[0]}
              </span>
              <svg className={`w-3.5 h-3.5 text-slate-400 transition-transform ${profileOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {profileOpen && (
              <div className="absolute right-0 w-56 mt-2 overflow-hidden bg-white border border-slate-200 shadow-xl top-full rounded-2xl">
                <div className="px-4 py-3 border-b border-slate-100">
                  <p className="font-medium truncate text-slate-800">{user?.full_name || "User"}</p>
                  <p className="text-sm truncate text-slate-500">{user?.email}</p>
                </div>
                <div className="p-2">
                  <button
                    onClick={() => {
                      setProfileOpen(false);
                      if (user?.id) navigate(`/admin/users/${user.id}`);
                    }}
                    className="flex items-center w-full gap-3 px-3 py-2 transition-colors rounded-lg text-slate-700 hover:bg-slate-50"
                  >
                    <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>{t("common.profile", "Profil")}</span>
                  </button>
                  {/* Coach, athlete, parent — whatever else this account holds.
                      Was coach-only, which left a club admin who also trains in
                      a squad with no way in. */}
                  <RoleModeSwitcher onNavigate={() => setProfileOpen(false)} />
                  <hr className="my-2 border-slate-100" />
                  <button
                    onClick={() => {
                      logout();
                      navigate("/login");
                    }}
                    className="flex items-center w-full gap-3 px-3 py-2 text-rose-600 transition-colors rounded-lg hover:bg-rose-50"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    <span>{t("common.logout", "Deconectare")}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
